import { action, comparer, computed, IReactionDisposer, makeObservable, observable, reaction } from 'mobx';

import { IApiClient } from '../../../app/data/client';
import { BasePageState } from '../../../app/shared/BasePageState';
import { debounce } from '../../../app/shared/common';
import Logger from '../../../app/shared/Logger';
import { INavigationService } from '../../../app/shared/NavigationService';
import { ICancellableTask, RunLastTasksScheduler } from '../../../app/shared/TasksScheduler';
import { RelativeUrl } from '../../../app/shared/Url';
import { SEARCH_DEBOUNCE_MS, type LoadingState } from '../../constants';
import { PricelistDetailsTranslation } from '../../localization/ModuleTranslation';
import type {
  BrowseCustomerPricelistItemsQueryResponse,
  CustomerPricelistData,
  CustomerPricelistItemSummaryData,
  GetCustomerPricelistMetadataQueryResponse,
  PricingContext,
} from '../../model';
import { ModuleRoutes } from '../../ModuleRoutes';
import {
  BrowseCustomerPricelistItemsQuery,
  DeleteCustomerPricelistsCommand,
  GetCustomerPricelistMetadataQuery,
  RemoveCustomerPricelistItemsCommand,
} from '../../queries';
import ConfirmationModalState from '../../shared/ConfirmationModal/ConfirmationModalState';
import ExportPricelistToExcelModalState from '../../shared/ExportPricelistToExcelModal/ExportPricelistToExcelModalState';
import ExportPricelistToPdfModalState from '../../shared/ExportPricelistToPdfModal/ExportPricelistToPdfModalState';
import EditPricelistFormState from '../../shared/PricelistForm/EditPricelistFormState';
import PricelistFormModalState from '../../shared/PricelistFormModal/PricelistFormModalState';
import { PricelistsQueryParser } from '../Pricelists/PricelistsQuery';
import { TAB_IDS } from '../Pricelists/PricelistsState';
import ExpandableProductAccessoriesState from './PricelistProduct/ExpandableProductAccessories/ExpandableProductAccessoriesState';

export interface PricelistDetailsItem {
  data: CustomerPricelistItemSummaryData;
  accessories: ExpandableProductAccessoriesState;
}

/** Products per request. A product card is tall, so a few fill the fold past the sentinel's prefetch margin. */
const PAGE_SIZE = 10;

/**
 * One request for a page of the collection's products, and whatever it brought back. `RunLastTasksScheduler`
 * flags the task `cancelled` as soon as a newer request starts, so once the run is awaited the caller can
 * tell an outcome that still matters from one a debounced reload has already superseded.
 */
class LoadItemsTask implements ICancellableTask {
  public cancelled = false;
  public response: BrowseCustomerPricelistItemsQueryResponse;
  /** Whatever the request rejected with — a `throw` is not obliged to carry an `Error`. */
  public error: unknown;

  public constructor(public readonly skip: number) {}
}

export default class PricelistDetailsState extends BasePageState<never> {
  @observable.ref private pricelist: GetCustomerPricelistMetadataQueryResponse | null = null;
  @observable.ref public items: PricelistDetailsItem[] = [];
  @observable public loadingState: LoadingState = 'loading';
  @observable public searchTerm = '';
  @observable public total = 0;

  /** One modal for every action on the page; what it is asking about is set when it is opened. */
  public readonly confirmationModal = new ConfirmationModalState();

  public readonly exportToExcelModal: ExportPricelistToExcelModalState;

  public readonly exportToPdfModal: ExportPricelistToPdfModalState;

  public readonly editPricelistModal: PricelistFormModalState<EditPricelistFormState>;

  private readonly loadItemsScheduler: RunLastTasksScheduler<LoadItemsTask, void>;
  private readonly disposeSearchReaction: IReactionDisposer;
  private isDisposed = false;

  public constructor(
    private readonly client: IApiClient,
    private readonly pricingContext: PricingContext,
    public readonly translation: PricelistDetailsTranslation,
    private readonly routes: ModuleRoutes,
    private readonly navigation: INavigationService,
    private readonly id: string,
  ) {
    super(translation);

    makeObservable(this);

    this.exportToExcelModal = new ExportPricelistToExcelModalState(this.client, this.pricingContext, this.id);
    this.exportToPdfModal = new ExportPricelistToPdfModalState(this.client, this.pricingContext, () => ({
      title: this.data.name,
      createdAt: this.data.createdAt,
      productIds: this.data.productModelIds,
      locale: this.locale,
    }));
    this.editPricelistModal = new PricelistFormModalState(
      this.translation.pricelistActions.edit.modal,
      new EditPricelistFormState(this.client, this.translation.pricelistActions.edit.modal.form),
      this.updatePricelist,
    );

    this.loadItemsScheduler = new RunLastTasksScheduler((task) => this.runLoadItemsTask(task));

    this.disposeSearchReaction = reaction(
      () => this.searchTerm,
      debounce(() => this.loadItems(), SEARCH_DEBOUNCE_MS),
      { equals: comparer.structural },
    );
  }

  public get currency(): string {
    return this.pricingContext.currency;
  }

  @computed
  public get region(): string {
    return this.pricelist.storeData.displayName;
  }

  @computed
  public get data(): CustomerPricelistData {
    return this.pricelist.pricelistData;
  }

  @computed
  public get canLoadMoreItems(): boolean {
    if (this.loadingState === 'loading' || this.loadingState === 'error') {
      return false;
    }

    return this.items.length < this.total;
  }

  public get backUrl(): RelativeUrl {
    const { referrer } = this.navigation;

    return this.routes.isListing(referrer)
      ? referrer
      : this.routes.listing(PricelistsQueryParser.toQuery({ tab: TAB_IDS.Pricelists }));
  }

  public get locale(): string {
    return this.pricingContext.languageCode;
  }

  /**
   * The metadata is awaited and the products are not: a collection that cannot be loaded — an unknown
   * id among them — reaches the host's error page before any product is asked for, while a failure
   * loading the products leaves the page rendered without them.
   */
  public async onLoad(): Promise<void> {
    const pricelist = await this.client.send(
      new GetCustomerPricelistMetadataQuery({ pricingContext: this.pricingContext, pricelistId: this.id }),
    );

    this.setPricelist(pricelist);
    this.loadItems();
  }

  public unload(): void {
    this.isDisposed = true;
    this.disposeSearchReaction();
    this.loadItemsScheduler.dispose();
  }

  @action.bound
  public async loadMoreItems(): Promise<void> {
    if (!this.canLoadMoreItems || this.loadingState === 'loadingMore') {
      return;
    }

    this.setLoadingState('loadingMore');

    const task = new LoadItemsTask(this.items.length);

    await this.loadItemsScheduler.run(task);

    if (task.cancelled) {
      return;
    }

    if (task.error) {
      Logger.warn(`Unable to load more price list products. ${task.error}`);
      this.setLoadingState('error');

      return;
    }

    if (task.response.items.empty()) {
      this.setTotal(this.items.length);
      this.setLoadingState('idle');

      return;
    }

    this.appendItems(task.response.items);
    this.setTotal(task.response.total);
    this.setLoadingState('idle');
  }

  @action.bound
  public setSearchTerm(searchTerm: string): void {
    this.searchTerm = searchTerm;
  }

  public deletePricelist(): void {
    const translation = this.translation.pricelistActions.delete;

    this.confirmationModal.ask({
      translation: translation.modal,
      message: translation.confirmationFormat.interpolate([['name', this.data.name]]),
      successMessage: translation.successFormat.interpolate([['name', this.data.name]]),
      run: async () => {
        await this.client.send(new DeleteCustomerPricelistsCommand({ pricelistIds: [this.id] }));
        this.navigation.navigateTo(this.backUrl);
      },
    });
  }

  public editPricelist() {
    this.editPricelistModal.open(this.data);
  }

  public removeItem(item: CustomerPricelistItemSummaryData): void {
    const translation = this.translation.productActions.remove;
    const name: [string, string][] = [['name', item.product?.name]];

    this.confirmationModal.ask({
      translation: translation.modal,
      message: translation.confirmationFormat.interpolate(name),
      successMessage: translation.successFormat.interpolate(name),
      run: async () => {
        const { pricelist } = await this.client.send(
          new RemoveCustomerPricelistItemsCommand({ pricelistId: this.id, productModelIds: [item.productModelId] }),
        );

        this.updatePricelist(pricelist);
        await this.loadItems();
      },
    });
  }

  @action.bound
  private async loadItems(): Promise<void> {
    // Disposing stops the reaction but cannot clear a debounced reload it has already scheduled,
    // so the last one can still land on a page the user has left.
    if (this.isDisposed) {
      return;
    }

    this.setLoadingState('loading');

    const task = new LoadItemsTask(0);

    await this.loadItemsScheduler.run(task);

    if (task.cancelled) {
      return;
    }

    if (task.error) {
      Logger.error(`Unable to load the price list products. Error: ${task.error}`);
      this.setItems([]);
      this.setTotal(0);
      this.setLoadingState('error');

      return;
    }

    this.setItems(task.response.items);
    this.setTotal(task.response.total);
    this.setLoadingState('idle');
  }

  private async runLoadItemsTask(task: LoadItemsTask): Promise<void> {
    try {
      task.response = await this.fetchItems(task.skip);
    } catch (error) {
      task.error = error;
    }
  }

  private async fetchItems(skip: number): Promise<BrowseCustomerPricelistItemsQueryResponse> {
    return this.client.send(
      new BrowseCustomerPricelistItemsQuery({
        pricingContext: this.pricingContext,
        pricelistId: this.id,
        searchPhrase: this.searchTerm,
        skip,
        take: PAGE_SIZE,
      }),
    );
  }

  private buildItems(items: CustomerPricelistItemSummaryData[]): PricelistDetailsItem[] {
    return items.map((data) => ({
      data,
      accessories: new ExpandableProductAccessoriesState(
        this.client,
        this.pricingContext,
        data.productModelId,
        data.accessoriesCount,
      ),
    }));
  }

  @action.bound
  private setItems(items: CustomerPricelistItemSummaryData[]): void {
    this.items = this.buildItems(items);
  }

  @action.bound
  private appendItems(items: CustomerPricelistItemSummaryData[]): void {
    this.items = [...this.items, ...this.buildItems(items)];
  }

  @action.bound
  private setLoadingState(loadingState: LoadingState): void {
    this.loadingState = loadingState;
  }

  @action.bound
  private setTotal(total: number): void {
    this.total = total;
  }

  @action.bound
  private setPricelist(pricelist: GetCustomerPricelistMetadataQueryResponse): void {
    this.pricelist = pricelist;
  }

  @action.bound
  private updatePricelist(pricelistData: CustomerPricelistData): void {
    this.pricelist = { ...this.pricelist, pricelistData };
  }
}
